import { Capacitor } from '@capacitor/core';

/**
 * The domain the brand prints on itself — downloaded posters, story cards, the
 * "find me at …" line, watermarks. Anything a person reads or a camera sees.
 *
 * This is deliberately NOT derived from window.location: partyresume.com still
 * serves the app, and a poster made there must still say the brand's home, not
 * the door someone happened to walk in through. Mirrors the server's
 * `BrandDomain` (Services/BrandDomain.cs), which stamps the same string on the
 * OG, ticket and share cards it renders.
 */
export const BRAND_DOMAIN = 'heyportal.app';
export const BRAND_URL = `https://${BRAND_DOMAIN}`;

/** `heyportal.app/events/ABC` — a share line, no scheme. */
export const brandPath = (path = '') => `${BRAND_DOMAIN}${path ? `/${path.replace(/^\/+/, '')}` : ''}`;

/**
 * The origin a link that leaves the app should carry. On the web that is the page's own
 * origin (partyresume.com keeps partyresume.com, localhost keeps localhost). Inside the native
 * shells the page's origin is `partyresume://localhost` (or `capacitor://localhost`) — a URL
 * nobody else can open — so it is the brand's home instead.
 */
export function webOrigin() {
  const o = typeof window !== 'undefined' && window.location ? window.location.origin : '';
  // Android's shell serves the bundle at `https://localhost` (capacitor.config `androidScheme:
  // https`) — an http(s) origin that passes the test below, so every share link an Android
  // phone handed out said "localhost". The shell itself knows it is one; ask it first.
  if (isNativeShell()) return BRAND_URL;
  return /^https?:\/\//.test(o) && !isBareLocalhost(o) ? o : BRAND_URL;
}

function isNativeShell() {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
}

/** `https://localhost` with no port is a WebView, not a dev server (`http://localhost:3000` keeps its port). */
function isBareLocalhost(origin) {
  try { const u = new URL(origin); return u.hostname === 'localhost' && !u.port; } catch { return false; }
}

/** `${webOrigin()}/events/ABC` — an absolute link for a share sheet, the clipboard, a QR. */
export const webUrl = (path = '') => `${webOrigin()}${path ? `/${path.replace(/^\/+/, '')}` : ''}`;
